import React, { useEffect, useState } from 'react';
import API from '../../api';
import { X, Search, Loader2, FolderInput } from 'lucide-react';

/**
 * Modale per cercare e importare una sezione da un altro preventivo.
 *
 * Props:
 *  - onClose()
 *  - onPick(sezioneClonata)  // riceve {nome, oggetto, iva, note, righe[]}
 */
export default function ImportSezioneModal({ onClose, onPick }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await API.get('/api/preventivi/sezioni/catalogo', { params: { q, limit: 50 } });
        setItems(r.data || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const handlePick = async (it) => {
    setImporting(`${it.preventivo_id}-${it.sezione_idx}`);
    try {
      const r = await API.get(`/api/preventivi/${it.preventivo_id}/sezione/${it.sezione_idx}`);
      onPick(r.data);
      onClose();
    } catch (e) {
      alert('Errore import: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImporting(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" data-testid="import-sezione-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <FolderInput size={18} className="text-purple-600" />
            <h3 className="font-bold">Importa sezione da preventivo esistente</h3>
          </div>
          <button onClick={onClose}><X size={20} className="text-slate-400" /></button>
        </div>

        <div className="p-4 border-b">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-slate-400" />
            <input autoFocus value={q} onChange={e => setQ(e.target.value)}
              placeholder="Cerca per nome sezione (es. 'Bagno', 'Tetto', 'Cantina')"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm"
              data-testid="sezione-search-input" />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            La sezione selezionata verrà <strong>clonata</strong> nel preventivo corrente. Modifiche successive non si propagano all'originale.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 p-3"><Loader2 size={16} className="animate-spin" /> Ricerca…</div>
          ) : items.length === 0 ? (
            <p className="text-center text-slate-400 italic py-6">Nessuna sezione trovata.</p>
          ) : (
            <div className="space-y-2">
              {items.map((it, i) => {
                const key = `${it.preventivo_id}-${it.sezione_idx}`;
                return (
                  <button key={key} onClick={() => handlePick(it)} disabled={importing === key}
                    data-testid={`pick-sezione-${i}`}
                    className="w-full text-left p-3 border border-slate-200 hover:border-purple-400 hover:bg-purple-50 rounded transition-all disabled:opacity-50">
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-bold text-sm">{it.nome}</p>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 bg-primary text-primary rounded">IVA {it.iva}%</span>
                    </div>
                    {it.oggetto && <p className="text-xs italic text-slate-600">{it.oggetto}</p>}
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1.5 flex-wrap">
                      <span>📄 {it.preventivo_titolo || it.numero_preventivo}</span>
                      <span>{it.n_righe} righe</span>
                      <span className="font-mono">€ {(Number(it.subtotale_imponibile) || 0).toFixed(2)}</span>
                      {it.preventivo_data && <span>· {it.preventivo_data}</span>}
                    </div>
                    {importing === key && <p className="text-xs text-purple-600 mt-1">Importazione in corso…</p>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
